import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus } from "lucide-react";
import { api, shortDate } from "../lib/api";
import { apiError, useAuth } from "../lib/auth";
import { PageHeader, Panel, NeonButton } from "../components/common";

const ROLE_INFO = {
  owner: "Full control: approve and submit, settings, team",
  analyst: "Draft, edit and stage representments; can't submit",
  viewer: "Read-only access to every module",
};
const sel = "bg-field border border-fieldline px-2 py-2 text-sm focus:outline-none focus:border-brand";
const link = (t) => `${window.location.origin}/invite/${t}`;
const copy = (t) => navigator.clipboard.writeText(link(t)).then(() => toast.success("Invite link copied")).catch(() => toast.message("Copy this invite link", { description: link(t), duration: 15000 }));

const InviteForm = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("analyst");
  const send = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/team/invites", { email, role }); setEmail(""); onDone(); copy(data.token); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={send} className="flex flex-wrap gap-3 items-end" data-testid="invite-form">
      <label className="flex-1 min-w-[220px]"><span className="label-xs">Teammate email</span>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${sel} w-full mt-1.5`} data-testid="invite-email-input" /></label>
      <label><span className="label-xs">Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value)} className={`${sel} block mt-1.5`} data-testid="invite-role-select">
          {Object.keys(ROLE_INFO).map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
      <NeonButton type="submit" data-testid="invite-send-btn"><UserPlus className="w-3.5 h-3.5" /> Create invite</NeonButton>
      <p className="w-full text-xs text-white/40">{ROLE_INFO[role]}. The invite link is copied so you can send it; it expires in 7 days.</p>
    </form>
  );
};

export default function Team() {
  const { user } = useAuth();
  const [d, setD] = useState({ members: [], invites: [] });
  const load = useCallback(() => api.get("/team").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const act = (p, msg) => p.then(() => { toast.success(msg); load(); }).catch((e) => toast.error(apiError(e)));

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow="// access control · roles" title="Team" />
      <Panel title="Invite a teammate" className="mb-6"><InviteForm onDone={load} /></Panel>
      <Panel title={`Members · ${d.members.length}`} className="mb-6">
        <ul className="divide-y divide-divider" data-testid="members-list">
          {d.members.map((m) => (
            <li key={m.id} className="py-3 flex flex-wrap items-center gap-4" data-testid={`member-${m.email}`}>
              <div className="flex-1 min-w-[200px]"><p className="text-sm">{m.name} {m.id === user.id && <span className="label-xs text-brand ml-2">you</span>}</p><p className="font-mono text-[11px] text-white/45">{m.email}</p></div>
              <select value={m.role} disabled={m.id === user.id} className={sel} data-testid={`member-role-${m.email}`}
                onChange={(e) => act(api.patch(`/team/members/${m.id}`, { role: e.target.value }), "Role updated")}>
                {Object.keys(ROLE_INFO).map((r) => <option key={r} value={r}>{r}</option>)}</select>
              {m.id !== user.id && <button onClick={() => act(api.delete(`/team/members/${m.id}`), "Member removed")} className="text-white/40 hover:text-[#F87171] transition-colors" data-testid={`member-remove-${m.email}`}><Trash2 className="w-4 h-4" /></button>}
            </li>))}
        </ul>
      </Panel>
      <Panel title={`Pending invites · ${d.invites.length}`}>
        {!d.invites.length && <p className="text-sm text-white/40" data-testid="no-invites">No pending invites.</p>}
        <ul className="divide-y divide-divider" data-testid="invites-list">
          {d.invites.map((i) => (
            <li key={i.id} className="py-3 flex flex-wrap items-center gap-4" data-testid={`invite-${i.email}`}>
              <div className="flex-1 min-w-[200px]"><p className="text-sm">{i.email} <span className="label-xs ml-2">{i.role}</span></p><p className="font-mono text-[11px] text-white/40">expires {shortDate(i.expires_at)}</p></div>
              <button onClick={() => copy(i.token)} className="flex items-center gap-1.5 font-mono text-[11px] text-white/60 hover:text-brand transition-colors" data-testid={`invite-copy-${i.email}`}><Copy className="w-3.5 h-3.5" /> COPY LINK</button>
              <button onClick={() => act(api.delete(`/team/invites/${i.id}`), "Invite revoked")} className="text-white/40 hover:text-[#F87171] transition-colors" data-testid={`invite-revoke-${i.email}`}><Trash2 className="w-4 h-4" /></button>
            </li>))}
        </ul>
      </Panel>
    </div>
  );
}
